import { useFindCrItems } from '@/components/CrItemsSelect/Provider';
import { useFindCrSubItems } from '@/components/CrSubItemsSelect/Provider';
import { useFindCurrencies } from '@/components/CurrenciesSelect/Provider';
import { useFindDepts } from '@/components/DeptSelect/Provider';
import { useFindExcise } from '@/components/ExciseSelect/Provider';
import { useFindExItems } from '@/components/ExItemSelect/Provider';
import { useFindProjectCodes } from '@/components/ProjectSelect/Provider';
import { INVOICE_NUMBER_UNSAVED } from '@/context/services/reportsType/invoiceReports/type';
import { ConvertInvoiceTransaction } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/model';
import { InvoiceTransaction } from '@/features/InvoiceReport/Edit/type';
import { toFloat } from '@/utils/convert/toNumber';
import { UploadFile, randomId } from '@moneyforward/ap-frontend-components';
import {
  NotFoundRecord,
  UnMatchHeader,
  importExcel,
  parse,
} from 'csv-excel-imp-exp-util';
import { stringToDayjs } from 'date-util';
import { useCallback } from 'react';
import {
  CustomInvoiceTransactionMapping,
  importMappings,
} from '../importExportMapping';
type ImportArgs = {
  file: UploadFile | undefined;
};

export { NotFoundRecord, UnMatchHeader };

const useConvertInvoiceTransactionMappingToInvoiceTransaction = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  hasWithholdingIncomeTax: boolean
) => {
  const { findExciseByName } = useFindExcise();
  const { findDeptByName } = useFindDepts();
  const { findCrItemsByName } = useFindCrItems();
  const { findCrSubItemsByName } = useFindCrSubItems();
  const { findExItemsByName } = useFindExItems();
  const { findProjectCodesByName } = useFindProjectCodes();
  const { findCurrenciesByName } = useFindCurrencies();
  return useCallback(
    (data: CustomInvoiceTransactionMapping[]): InvoiceTransaction[] => {
      return data.map((item): InvoiceTransaction => {
        const excise = findExciseByName(item.exciseName);
        const dept = findDeptByName(item.deptName);
        const crItem = findCrItemsByName(item.crItemName);
        const crSubItem = findCrSubItemsByName(item.crSubItemName, crItem);
        const exItem = findExItemsByName(item.exItemName);
        const project = findProjectCodesByName(item.projectName);
        const currency = findCurrenciesByName(item.currency);
        const value = {
          id: 'imp' + String(Math.floor(Math.random() * 10000)),
          number: INVOICE_NUMBER_UNSAVED,
          dealDate: stringToDayjs(item.dealDate) ?? null,
          name: item.name,
          exItemId: exItem,
          exItemName: item.exItemName,
          unit: toFloat(item.unitPriceExcludingTax),
          taxIncludedUnit: toFloat(item.unitPriceIncludingTax),
          quantity: toFloat(item.quantity),
          totalValue: toFloat(item.totalValueExcludingTax),
          taxIncludedTotalValue: toFloat(item.totalValueIncludingTax),
          drExciseId: excise,
          drExciseName: item.exciseName,
          invoiceKind: item.invoiceKind ? item.invoiceKind : '',
          exciseValue: toFloat(item.tax),
          withholdingIncomeTax: toFloat(item.withholdingIncomeTax),
          hasWithholdingIncomeTax: !hasWithholdingIncomeTax
            ? false
            : !!item.withholdingIncomeTax,
          memo: item.memo,
          deptId: dept,
          deptName: item.deptName,
          projectCodeId: project,
          projectCodeName: item.projectName,
          currency: currency,
          jpyRate: toFloat(item.jpyRate),
          crItemId: crItem,
          crItemName: item.crItemName,
          crSubItemId: crSubItem,
          crSubItemName: item.crSubItemName,
          rowKey: randomId('invoice-transaction', 8),
        } satisfies Omit<InvoiceTransaction, 'isCurrencyJPY'>;
        if (!invoiceTransactionDetails) {
          value.quantity = 1;
          const totalValue = taxInclude
            ? value.taxIncludedTotalValue - value.exciseValue
            : value.totalValue;
          value.unit = totalValue;
          value.taxIncludedUnit = totalValue;
        }
        // NOTE: 税込場合 数,単 の入力は受け付けない
        if (taxInclude) {
          // 数量, 単価(税込), 単価(税抜)
          value.quantity = 1;
          value.taxIncludedUnit = value.taxIncludedTotalValue;
          const totalValue = value.taxIncludedTotalValue - value.exciseValue; // 税込金額 - 税額
          value.unit = totalValue;
          value.totalValue = totalValue;
        }
        return ConvertInvoiceTransaction(value);
      });
    },
    [
      findCrItemsByName,
      findCrSubItemsByName,
      findCurrenciesByName,
      findDeptByName,
      findExItemsByName,
      findExciseByName,
      findProjectCodesByName,
      hasWithholdingIncomeTax,
      invoiceTransactionDetails,
      taxInclude,
    ]
  );
};

export const useImport = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  isAdminOrApproval: boolean,
  hasWithholdingIncomeTax: boolean,
  canDisplayDrExcise: boolean
) => {
  const convertInvoiceTransaction =
    useConvertInvoiceTransactionMappingToInvoiceTransaction(
      taxInclude,
      invoiceTransactionDetails,
      hasWithholdingIncomeTax
    );
  const importInvoiceTransaction = useCallback(
    async ({ file }: ImportArgs): Promise<InvoiceTransaction[]> => {
      return new Promise((resolve, reject) => {
        if (!file) {
          resolve([]);
          return;
        }
        try {
          const isCsv = file.type === 'text/csv';
          const parseFn = isCsv
            ? parse<CustomInvoiceTransactionMapping>
            : importExcel<CustomInvoiceTransactionMapping>;
          const reader: FileReader = new FileReader();
          reader.onloadend = async (e) => {
            try {
              if (!e.target?.result) {
                return resolve([]);
              }
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              const input = Buffer.from(e.target.result as ArrayBuffer);
              const data: CustomInvoiceTransactionMapping[] = await parseFn(
                // @ts-ignore
                input,
                importMappings(
                  isAdminOrApproval,
                  invoiceTransactionDetails,
                  taxInclude,
                  hasWithholdingIncomeTax,
                  canDisplayDrExcise
                )
              );
              resolve(convertInvoiceTransaction(data));
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = (e) => {
            reject(e);
          };
          if (file.originFileObj) {
            const blob = new Blob([file.originFileObj]);
            reader.readAsArrayBuffer(blob);
          }
        } catch (error) {
          reject(error);
        }
      });
    },
    [
      convertInvoiceTransaction,
      hasWithholdingIncomeTax,
      invoiceTransactionDetails,
      isAdminOrApproval,
      taxInclude,
      canDisplayDrExcise,
    ]
  );
  return { importInvoiceTransaction };
};
